import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Receipt, Plus, Undo2, AlertTriangle, Pencil, Archive } from "lucide-react";
import { DeleteTransaction, RestoreButton } from "./TransactionDelete";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Card, LoadingBlock, EmptyState, formatDateTime, formatCurrency } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const TYPES = ["deposit", "deposit_correction", "refund", "service_charge", "service_payment", "admin_correction", "migration_opening", "withdrawal"];

const toLocalInput = (d = new Date()) => {
  const dt = (d instanceof Date && !isNaN(d)) ? d : new Date();
  const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const toISO = (localStr) => { try { return localStr ? new Date(localStr).toISOString() : undefined; } catch { return undefined; } };

export default function AdminTransactions() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? en ?? it) : it);

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [direction, setDirection] = useState("");
  const [status, setStatus] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    api.get("/admin/clients", { params: { page_size: 200 } }).then(({ data }) => setClients(data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/transactions", {
      params: {
        q: q || undefined, entry_type: type || undefined, direction: direction || undefined,
        status: status || undefined, client_id: clientId || undefined, page, page_size: 20,
        deleted: showDeleted || undefined,
      },
    });
    setItems(data.items); setTotal(data.total);
  }, [q, type, direction, status, clientId, page, showDeleted]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const selectCls = "h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-medium text-white">{L("Transazioni", "Transactions", "Transaktionen")}</h2>
        {isSuper && <NewTransaction clients={clients} onDone={() => { setPage(1); load(); }} L={L} />}
      </div>

      <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder={L("Cerca cliente, riferimento o motivo…", "Search client, reference or reason…", "Kunde, Referenz oder Grund suchen…")} className="pl-9 bg-navy-paper border-white/10" data-testid="atx-search" />
        </div>
        <select value={clientId} onChange={(e) => { setPage(1); setClientId(e.target.value); }} className={selectCls} data-testid="atx-client-filter">
          <option value="">{L("Tutti i clienti", "All clients", "Alle Kunden")}</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.email}</option>)}
        </select>
        <select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} className={selectCls} data-testid="atx-type-filter">
          <option value="">{L("Tutti i tipi", "All types", "Alle Typen")}</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={direction} onChange={(e) => { setPage(1); setDirection(e.target.value); }} className={selectCls} data-testid="atx-direction-filter">
          <option value="">{L("Tutte le direzioni", "All directions", "Alle Richtungen")}</option>
          <option value="credit">{L("Accredito", "Credit", "Gutschrift")}</option>
          <option value="debit">{L("Addebito", "Debit", "Belastung")}</option>
        </select>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className={selectCls} data-testid="atx-status-filter">
          <option value="">{L("Tutti gli stati", "All statuses", "Alle Status")}</option>
          <option value="confirmed">{L("Confermata", "Confirmed", "Bestätigt")}</option><option value="pending">{L("In attesa", "Pending", "Ausstehend")}</option>
        </select>
        <Button variant="outline" onClick={() => { setPage(1); setShowDeleted((v) => !v); }} className={`h-10 border-white/20 ${showDeleted ? "bg-red-500/15 text-red-300 border-red-500/40" : "text-slate-300"}`} data-testid="atx-toggle-deleted">
          <Archive className="h-4 w-4 mr-1.5" />{showDeleted ? L("Mostra attive", "Show active", "Aktive anzeigen") : L("Mostra eliminate", "Show deleted", "Gelöschte anzeigen")}
        </Button>
      </div>
      {showDeleted && <p className="text-xs text-red-300/80" data-testid="atx-deleted-hint">{L("Transazioni eliminate: non visibili al cliente. Il saldo non è stato modificato.", "Deleted transactions: hidden from the client. The balance was not changed.", "Gelöschte Transaktionen: für den Kunden unsichtbar. Der Saldo wurde nicht verändert.")}</p>}

      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={Receipt} title={L("Nessuna transazione", "No transactions", "Keine Transaktionen")} /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">{L("Cliente", "Client", "Kunde")}</th>
                <th className="text-left px-4 py-3">{L("Tipo", "Type", "Typ")}</th>
                <th className="text-left px-4 py-3">{L("Importo", "Amount", "Betrag")}</th>
                <th className="text-left px-4 py-3">{L("Direzione", "Direction", "Richtung")}</th>
                <th className="text-left px-4 py-3">{L("Stato", "Status", "Status")}</th>
                <th className="text-left px-4 py-3">{L("Data", "Date", "Datum")}</th>
                <th className="text-left px-4 py-3">{L("Admin", "Admin", "Admin")}</th>
                <th className="text-left px-4 py-3">{L("Riferimento", "Reference", "Referenz")}</th>
                <th className="text-right px-4 py-3">{L("Azioni", "Actions", "Aktionen")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`atx-row-${e.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-200">{e.client_name || "—"}</div>
                    <div className="text-xs text-slate-500">{e.client_email || e.client_id?.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-300">{e.entry_type}{e.reversed && <span className="ml-1.5 text-[10px] uppercase text-amber-400 border border-amber-400/40 rounded px-1 py-0.5">{L("stornata", "reversed", "storniert")}</span>}{e.reversal_of && <span className="ml-1.5 text-[10px] uppercase text-cyanx border border-cyanx/40 rounded px-1 py-0.5">{L("storno", "reversal", "Storno")}</span>}
                    {e.deleted && <div className="mt-1 text-[11px] text-red-300" data-testid={`atx-deleted-info-${e.id}`}>{L("Eliminata", "Deleted", "Gelöscht")} {formatDateTime(e.deleted_at)} · {e.deleted_by}<div className="text-slate-500">{e.deletion_reason}</div></div>}</td>
                  <td className="px-4 py-2.5 font-mono text-white">{formatCurrency(e.amount, e.currency)}</td>
                  <td className="px-4 py-2.5"><span className={e.direction === "credit" ? "text-emerald-400" : e.direction === "debit" ? "text-red-400" : "text-slate-500"}>{e.direction}</span></td>
                  <td className="px-4 py-2.5 text-slate-400">{e.status}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                    <div className="text-slate-300" data-testid={`atx-effective-${e.id}`}>{formatDateTime(e.effective_at || e.created_at)}</div>
                    {e.effective_at && e.created_at && e.effective_at.slice(0, 16) !== e.created_at.slice(0, 16) && (
                      <div className="text-[10px] text-slate-600">{L("creato", "created", "erstellt")}: {formatDateTime(e.created_at)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{e.created_by || "—"}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{e.external_reference || "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isSuper && !e.deleted && !e.reversed && !e.reversal_of && (
                        <EditTransaction entry={e} onDone={load} L={L} />
                      )}
                      {isSuper && !e.deleted && e.status === "confirmed" && ["credit", "debit"].includes(e.direction) && !e.reversed && !e.reversal_of && (
                        <ReverseButton entry={e} onDone={load} L={L} />
                      )}
                      {isSuper && e.deleted && <RestoreButton entry={e} onDone={load} L={L} />}
                      {(!e.deleted || isSuper) && <DeleteTransaction entry={e} isSuper={isSuper} onDone={load} L={L} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {total > 20 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{total} {L("risultati", "results", "Ergebnisse")}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-white/20" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="atx-prev">{L("Precedente", "Previous", "Zurück")}</Button>
            <Button size="sm" variant="outline" className="border-white/20" disabled={page * 20 >= total} onClick={() => setPage((p) => p + 1)} data-testid="atx-next">{L("Successiva", "Next", "Weiter")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const NT_INITIAL = { client_id: "", entry_type: "admin_correction", direction: "credit", amount: "", currency: "EUR", status: "confirmed", description: "", admin_reason: "", external_reference: "" };

function NewTransaction({ clients, onDone, L }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => ({ ...NT_INITIAL, effective_at: toLocalInput() }));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idem] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())));

  const amount = parseFloat(f.amount) || 0;
  const canPreview = f.client_id && amount > 0;

  useEffect(() => {
    if (!open || !canPreview) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/transactions/preview", { client_id: f.client_id, direction: f.direction, amount, status: f.status });
        setPreview(data);
      } catch { setPreview(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, f.client_id, f.direction, amount, f.status, canPreview]);

  const now = new Date().toLocaleString();

  const willNeg = preview?.would_negative;
  const invalid = !f.client_id || amount <= 0 || !f.description.trim() || !f.admin_reason.trim() || willNeg || busy;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/transactions", {
        client_id: f.client_id, entry_type: f.entry_type, direction: f.direction,
        amount, currency: f.currency, status: f.status,
        description: f.description, admin_reason: f.admin_reason,
        effective_at: toISO(f.effective_at),
        external_reference: f.external_reference || undefined, idempotency_key: idem,
      });
      toast.success(L("Transazione registrata", "Transaction posted", "Transaktion gebucht"));
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "bg-navy border-white/10";
  const selCls = "w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF({ ...NT_INITIAL, effective_at: toLocalInput() }); setPreview(null); } }}>
      <DialogTrigger asChild>
        <Button className="bg-gold text-navy hover:bg-gold-hover" data-testid="atx-new-btn"><Plus className="h-4 w-4 mr-1.5" />{L("Nuova transazione", "New transaction", "Neue Transaktion")}</Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10 max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{L("Nuova transazione", "New transaction", "Neue Transaktion")}</DialogTitle>
          <DialogDescription className="text-slate-400">{L("Crea un movimento reale nel libro mastro. Il saldo non può diventare negativo.", "Creates a real ledger entry. Balance cannot go negative.", "Erstellt eine echte Hauptbuchbuchung. Der Saldo kann nicht negativ werden.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400">{L("Cliente", "Client", "Kunde")}</label>
            <select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={selCls} data-testid="atx-nt-client">
              <option value="">{L("Seleziona cliente…", "Select client…", "Kunde auswählen…")}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.email}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">{L("Tipo", "Type", "Typ")}</label>
              <select value={f.entry_type} onChange={(e) => setF({ ...f, entry_type: e.target.value })} className={selCls} data-testid="atx-nt-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">{L("Direzione", "Direction", "Richtung")}</label>
              <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} className={selCls} data-testid="atx-nt-direction">
                <option value="credit">{L("Accredito", "Credit", "Gutschrift")}</option>
                <option value="debit">{L("Addebito", "Debit", "Belastung")}</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">{L("Importo", "Amount", "Betrag")}</label>
              <Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inputCls} data-testid="atx-nt-amount" />
            </div>
            <div>
              <label className="text-xs text-slate-400">{L("Valuta", "Currency", "Währung")}</label>
              <Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className={inputCls} data-testid="atx-nt-currency" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Stato", "Status", "Status")}</label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={selCls} data-testid="atx-nt-status">
              <option value="confirmed">{L("Confermata (aggiorna il saldo)", "Confirmed (updates balance)", "Bestätigt (aktualisiert Saldo)")}</option>
              <option value="pending">{L("In sospeso (non aggiorna il saldo)", "Pending (does not update balance)", "Ausstehend (aktualisiert Saldo nicht)")}</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Data e ora della transazione", "Transaction date & time", "Transaktionsdatum & -zeit")}</label>
            <Input type="datetime-local" value={f.effective_at} onChange={(e) => setF({ ...f, effective_at: e.target.value })} className={inputCls} data-testid="atx-nt-effective" />
            <p className="text-[11px] text-slate-500 mt-1">{L("Mostrata al cliente. Può essere nel passato.", "Shown to the client. Can be in the past.", "Wird dem Kunden angezeigt. Kann in der Vergangenheit liegen.")}</p>
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Descrizione visibile al cliente", "Client-visible description", "Für Kunden sichtbare Beschreibung")}</label>
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} data-testid="atx-nt-description" />
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Motivo amministrativo (obbligatorio, interno)", "Administrative reason (required, internal)", "Verwaltungsgrund (erforderlich, intern)")}</label>
            <Textarea value={f.admin_reason} onChange={(e) => setF({ ...f, admin_reason: e.target.value })} rows={2} className={inputCls} data-testid="atx-nt-reason" />
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Riferimento esterno (facoltativo)", "External reference (optional)", "Externe Referenz (optional)")}</label>
            <Input value={f.external_reference} onChange={(e) => setF({ ...f, external_reference: e.target.value })} className={inputCls} data-testid="atx-nt-reference" />
          </div>

          {canPreview && preview && (
            <div className="rounded-lg border border-white/10 bg-navy p-3 text-sm space-y-1.5" data-testid="atx-preview">
              <div className="flex justify-between"><span className="text-slate-400">{L("Saldo attuale", "Current balance", "Aktueller Saldo")}</span><span className="text-slate-200 font-mono">{preview.current.toFixed(2)} {preview.currency}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">{f.direction === "credit" ? L("Accredito", "Credit", "Gutschrift") : L("Addebito", "Debit", "Belastung")}</span><span className={`font-mono ${f.direction === "credit" ? "text-emerald-400" : "text-red-400"}`}>{f.direction === "credit" ? "+" : "−"}{amount.toFixed(2)} {preview.currency}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-1.5"><span className="text-slate-400">{L("Saldo previsto", "Projected balance", "Voraussichtlicher Saldo")}</span><span className={`font-mono ${willNeg ? "text-red-500" : "text-white"}`} data-testid="atx-projected">{preview.projected.toFixed(2)} {preview.currency}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-500">{L("Data e ora", "Date & time", "Datum & Uhrzeit")}</span><span className="text-slate-400">{f.effective_at ? new Date(f.effective_at).toLocaleString() : now}</span></div>
              {f.description.trim() && <div className="text-xs text-slate-500 border-t border-white/10 pt-1.5">{L("Visibile al cliente", "Visible to client", "Für Kunden sichtbar")}: <span className="text-slate-300">{f.description}</span></div>}
              {preview.ledger_status === "pending" && <p className="text-xs text-amber-400">{L("In sospeso: non modifica il saldo finché non viene confermata.", "Pending: does not change balance until confirmed.", "Ausstehend: ändert den Saldo erst nach Bestätigung.")}</p>}
            </div>
          )}
          {willNeg && <p className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />{L("L'addebito supera il saldo disponibile.", "Debit exceeds available balance.", "Die Belastung übersteigt den verfügbaren Saldo.")}</p>}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={invalid} className="bg-gold text-navy hover:bg-gold-hover" data-testid="atx-nt-save">{busy ? L("Attendere…", "Please wait…", "Bitte warten…") : L("Registra transazione", "Post transaction", "Transaktion buchen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReverseButton({ entry, onDone, L }) {  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/transactions/${entry.id}/reverse`, { reason });
      toast.success(L("Transazione stornata", "Transaction reversed", "Transaktion storniert"));
      setOpen(false); setReason("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setReason(""); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 h-8" data-testid={`atx-reverse-${entry.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" />{L("Storna", "Reverse", "Stornieren")}</Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader>
          <DialogTitle>{L("Storno transazione", "Reverse transaction", "Transaktion stornieren")}</DialogTitle>
          <DialogDescription className="text-slate-400">{L("Crea una voce opposta collegata all'originale. La transazione originale non viene cancellata né modificata.", "Creates an opposite entry linked to the original. The original transaction is not deleted or modified.", "Erstellt eine Gegenbuchung, die mit dem Original verknüpft ist. Die Originaltransaktion wird nicht gelöscht oder geändert.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-white/10 bg-navy p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-400">{entry.entry_type}</span><span className="font-mono text-white">{formatCurrency(entry.amount, entry.currency)}</span></div>
            <div className="flex justify-between text-xs"><span className="text-slate-500">{L("Direzione originale", "Original direction", "Ursprüngliche Richtung")}</span><span className={entry.direction === "credit" ? "text-emerald-400" : "text-red-400"}>{entry.direction}</span></div>
            <div className="flex justify-between text-xs"><span className="text-slate-500">{L("Storno", "Reversal", "Storno")}</span><span className={entry.direction === "credit" ? "text-red-400" : "text-emerald-400"}>{entry.direction === "credit" ? "debit" : "credit"} {formatCurrency(entry.amount, entry.currency)}</span></div>
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Motivo dello storno (obbligatorio)", "Reversal reason (required)", "Stornierungsgrund (erforderlich)")}</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="bg-navy border-white/10" data-testid="atx-reverse-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!reason.trim() || busy} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="atx-reverse-confirm">{busy ? L("Attendere…", "Please wait…", "Bitte warten…") : L("Conferma storno", "Confirm reversal", "Storno bestätigen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditTransaction({ entry, onDone, L }) {
  const posted = entry.status === "confirmed";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const init = () => ({
    effective_at: toLocalInput(new Date(entry.effective_at || entry.created_at)),
    description: entry.reason || "",
    external_reference: entry.external_reference || "",
    internal_note: entry.internal_note || "",
    amount: String(entry.amount),
    currency: entry.currency,
    direction: entry.direction,
    status: entry.status,
    edit_reason: "",
  });
  const [f, setF] = useState(init);

  const newAmount = parseFloat(f.amount) || 0;
  const amountChanged = !posted && newAmount !== Number(entry.amount);
  const dirChanged = !posted && f.direction !== entry.direction;
  const statusChanged = f.status !== entry.status;

  useEffect(() => {
    if (!open || posted || (!amountChanged && !dirChanged && !statusChanged)) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/transactions/preview", { client_id: entry.client_id, direction: f.direction, amount: newAmount, status: f.status });
        setPreview(data);
      } catch { setPreview(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, posted, f.direction, f.status, newAmount, amountChanged, dirChanged, statusChanged, entry.client_id]);

  const willNeg = preview?.would_negative;
  const invalid = !f.edit_reason.trim() || !f.description.trim() || busy || willNeg || (!posted && newAmount <= 0);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        edit_reason: f.edit_reason,
        effective_at: toISO(f.effective_at),
        description: f.description,
        external_reference: f.external_reference,
        internal_note: f.internal_note,
      };
      if (!posted) {
        payload.amount = newAmount;
        payload.currency = f.currency;
        payload.direction = f.direction;
        payload.status = f.status;
      }
      await api.patch(`/admin/transactions/${entry.id}`, payload);
      toast.success(L("Transazione aggiornata", "Transaction updated", "Transaktion aktualisiert"));
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "bg-navy border-white/10";
  const selCls = "w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white disabled:opacity-50";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setF(init()); else setPreview(null); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-white/20 text-slate-200 h-8" data-testid={`atx-edit-${entry.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{L("Modifica", "Edit", "Bearbeiten")}</Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10 max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{L("Modifica transazione", "Edit transaction", "Transaktion bearbeiten")}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {posted
              ? L("Transazione contabilizzata: importo, valuta e direzione si correggono solo con lo storno.", "Posted transaction: amount, currency and direction can only be corrected via reversal.", "Gebuchte Transaktion: Betrag, Währung und Richtung können nur per Storno korrigiert werden.")
              : L("Transazione in sospeso: tutti i campi sono modificabili.", "Pending transaction: all fields are editable.", "Ausstehende Transaktion: alle Felder sind bearbeitbar.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400">{L("Data e ora effettiva", "Effective date & time", "Effektives Datum & Zeit")}</label>
            <Input type="datetime-local" value={f.effective_at} onChange={(e) => setF({ ...f, effective_at: e.target.value })} className={inputCls} data-testid="atx-edit-effective" />
            <p className="text-[11px] text-slate-500 mt-1">{L("Reale creazione", "Actually created", "Tatsächlich erstellt")}: {formatDateTime(entry.created_at)} · {entry.created_by}</p>
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Descrizione visibile al cliente", "Client-visible description", "Für Kunden sichtbare Beschreibung")}</label>
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} data-testid="atx-edit-description" />
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Riferimento / TXID", "Reference / TXID", "Referenz / TXID")}</label>
            <Input value={f.external_reference} onChange={(e) => setF({ ...f, external_reference: e.target.value })} className={inputCls} data-testid="atx-edit-reference" />
          </div>
          <div>
            <label className="text-xs text-slate-400">{L("Nota interna", "Internal note", "Interne Notiz")}</label>
            <Textarea value={f.internal_note} onChange={(e) => setF({ ...f, internal_note: e.target.value })} rows={2} className={inputCls} data-testid="atx-edit-internal-note" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">{L("Importo", "Amount", "Betrag")}</label>
              <Input type="number" min="0" step="0.01" value={f.amount} disabled={posted} onChange={(e) => setF({ ...f, amount: e.target.value })} className={`${inputCls} disabled:opacity-50`} data-testid="atx-edit-amount" />
            </div>
            <div>
              <label className="text-xs text-slate-400">{L("Valuta", "Currency", "Währung")}</label>
              <Input value={f.currency} disabled={posted} onChange={(e) => setF({ ...f, currency: e.target.value })} className={`${inputCls} disabled:opacity-50`} data-testid="atx-edit-currency" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">{L("Direzione", "Direction", "Richtung")}</label>
              <select value={f.direction} disabled={posted} onChange={(e) => setF({ ...f, direction: e.target.value })} className={selCls} data-testid="atx-edit-direction">
                <option value="credit">{L("Accredito", "Credit", "Gutschrift")}</option>
                <option value="debit">{L("Addebito", "Debit", "Belastung")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">{L("Stato", "Status", "Status")}</label>
              <select value={f.status} disabled={posted} onChange={(e) => setF({ ...f, status: e.target.value })} className={selCls} data-testid="atx-edit-status">
                <option value="pending">{L("In attesa", "Pending", "Ausstehend")}</option>
                <option value="confirmed">{L("Confermata", "Confirmed", "Bestätigt")}</option>
                <option value="cancelled">{L("Annullata", "Cancelled", "Storniert")}</option>
              </select>
            </div>
          </div>

          {preview && (amountChanged || dirChanged || statusChanged) && (
            <div className="rounded-lg border border-white/10 bg-navy p-3 text-sm space-y-1.5" data-testid="atx-edit-preview">
              <div className="flex justify-between"><span className="text-slate-400">{L("Saldo attuale", "Current balance", "Aktueller Saldo")}</span><span className="text-slate-200 font-mono">{preview.current.toFixed(2)} {preview.currency}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">{L("Vecchio importo", "Old amount", "Alter Betrag")}</span><span className="font-mono text-slate-400">{Number(entry.amount).toFixed(2)} {entry.currency}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">{L("Nuovo importo", "New amount", "Neuer Betrag")}</span><span className="font-mono text-white">{newAmount.toFixed(2)} {f.currency}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-1.5"><span className="text-slate-400">{L("Saldo previsto", "Projected balance", "Voraussichtlicher Saldo")}</span><span className={`font-mono ${willNeg ? "text-red-500" : "text-white"}`} data-testid="atx-edit-projected">{preview.projected.toFixed(2)} {preview.currency}</span></div>
            </div>
          )}
          {willNeg && <p className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />{L("La modifica renderebbe il saldo negativo.", "The change would make the balance negative.", "Die Änderung würde den Saldo negativ machen.")}</p>}

          <div>
            <label className="text-xs text-slate-400">{L("Motivo della modifica (obbligatorio)", "Reason for change (required)", "Grund der Änderung (erforderlich)")}</label>
            <Textarea value={f.edit_reason} onChange={(e) => setF({ ...f, edit_reason: e.target.value })} rows={2} className={inputCls} data-testid="atx-edit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={invalid} className="bg-gold text-navy hover:bg-gold-hover" data-testid="atx-edit-save">{busy ? L("Attendere…", "Please wait…", "Bitte warten…") : L("Salva modifiche", "Save changes", "Änderungen speichern")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
